/**
 * WorkflowForm Component
 * Form for creating/editing scheduled workflows
 */

import { useState, useEffect } from 'preact/hooks';
import { useScopeContext } from '../../contexts/ScopeContext';
import { useWebSocketContext } from '../../contexts/WebSocketContext';
import { agentsService } from '../../services/agents.service';
import { scheduledWorkflowsService } from '../../services/scheduled-workflows.service';
import type { Agent } from '@zenmako/contracts/agents';
import type { CreateWorkflowInput, TodoItem, SupportedModel } from '../../services/scheduled-workflows.service';
import { SchedulePicker } from './SchedulePicker';
import { toast } from 'react-toastify';

interface WorkflowFormProps {
  initialData?: CreateWorkflowInput & { workflowId?: string };
  onSubmit: (data: CreateWorkflowInput) => Promise<void>;
  onCancel: () => void;
  submitLabel?: string;
}

export function WorkflowForm({
  initialData,
  onSubmit,
  onCancel,
  submitLabel = 'Create Workflow',
}: WorkflowFormProps) {
  const scope = useScopeContext();
  const { subscribe } = useWebSocketContext();
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loadingAgents, setLoadingAgents] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [supportedModels, setSupportedModels] = useState<SupportedModel[]>([]);

  // Form state — single object to avoid Preact hook sync issues with individual useState
  const [form, setForm] = useState({
    name: initialData?.name || '',
    description: initialData?.description || '',
    agentId: initialData?.agentId || '',
    prompt: initialData?.prompt || '',
    primaryModel: initialData?.primaryModel || '',
    secondaryModel: initialData?.secondaryModel || '',
    enabled: initialData?.enabled ?? true,
    schedule: initialData?.schedule || {
      type: 'daily' as const,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
  });
  const updateField = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
    setForm(prev => ({ ...prev, [key]: value }));

  // Preview state
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewTodos, setPreviewTodos] = useState<TodoItem[]>([]);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    loadAgents();
    loadSupportedModels();
  }, [scope.teamId]);

  const loadAgents = async () => {
    try {
      setLoadingAgents(true);
      const agentsList = await agentsService.getAgents(scope.teamId);
      setAgents(agentsList);

      // Auto-select first agent if none selected
      if (!form.agentId && agentsList.length > 0) {
        updateField('agentId', agentsList[0].id);
      }
    } catch (err) {
      console.error('Failed to load agents:', err);
      toast.error('Failed to load agents');
    } finally {
      setLoadingAgents(false);
    }
  };

  const loadSupportedModels = async () => {
    try {
      const models = await scheduledWorkflowsService.getSupportedModels();
      setSupportedModels(models);
    } catch (err) {
      console.error('Failed to load supported models:', err);
    }
  };

  const handlePreview = async () => {
    if (!form.prompt.trim() || !form.agentId) {
      toast.error('Please enter a prompt and select an agent');
      return;
    }

    try {
      setPreviewLoading(true);

      // Get threadId for workflow preview
      const result = await scheduledWorkflowsService.previewWorkflow(
        scope.teamId,
        form.prompt,
        form.agentId
      );

      const previewThreadId = result.threadId;

      // Subscribe to WebSocket for this threadId
      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => {
          unsubscribe();
          reject(new Error('Workflow preview timed out'));
        }, 60000); // 60 second timeout - AI generation can take a while

        const unsubscribe = subscribe((message) => {
          // Filter for this specific threadId
          if (message.payload?.threadId !== previewThreadId) {
            return;
          }

          // Filter for thread.message type (mapped from chat/response@v1)
          if (message.messageType !== 'thread.message') {
            return;
          }

          // Filter for chat/response@v1 specifically (not chat/message@v1 which echoes the prompt)
          // chat/message@v1 has content as string, chat/response@v1 has content as array
          if (message.payload?.type !== 'chat/response@v1') {
            return;
          }

          // Extract tool calls from content
          const content = message.payload?.content;
          if (!Array.isArray(content)) {
            return;
          }

          // Find the initializeTodolist tool call
          const toolCall = content.find(
            (part: any) =>
              part.type === 'tool-call' &&
              part.toolName === 'core_initializeTodolist'
          );

          if (!toolCall || !toolCall.input?.todoList) {
            return;
          }

          // Extract TODO list from tool call arguments
          const todos = toolCall.input.todoList.map((todo: any) => ({
            description: todo.description || todo.title,
            requiresApproval: todo.requiresApproval || false,
          }));

          // Display the preview
          setPreviewTodos(todos);
          setShowPreview(true);

          // Cleanup
          clearTimeout(timeout);
          unsubscribe();
          setPreviewLoading(false);
          resolve();
        });
      });
    } catch (err) {
      console.error('Failed to generate preview:', err);
      toast.error('Failed to generate preview');
      setPreviewLoading(false);
    }
  };

  const handleSubmit = async (e: Event) => {
    e.preventDefault();

    // Validation
    if (!form.name.trim()) {
      toast.error('Please enter a workflow name');
      return;
    }
    if (!form.agentId) {
      toast.error('Please select an agent');
      return;
    }
    if (!form.prompt.trim()) {
      toast.error('Please enter a prompt');
      return;
    }
    if (form.schedule.type === 'one-off' && !form.schedule.executeAt) {
      toast.error('Please select a date/time for one-off workflows');
      return;
    }
    if (form.schedule.type === 'cron' && !form.schedule.expression) {
      toast.error('Please enter a cron expression');
      return;
    }

    const payload: CreateWorkflowInput = {
      name: form.name,
      description: form.description,
      agentId: form.agentId,
      prompt: form.prompt,
      schedule: form.schedule,
      enabled: form.enabled,
      primaryModel: form.primaryModel,
      secondaryModel: form.secondaryModel,
    };

    try {
      setSubmitting(true);
      await onSubmit(payload);
    } catch (err) {
      console.error('Failed to submit workflow:', err);
      toast.error('Failed to save workflow');
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingAgents) {
    return (
      <div class="p-8 flex justify-center">
        <span class="loading loading-spinner loading-lg"></span>
      </div>
    );
  }

  if (agents.length === 0) {
    return (
      <div class="p-8">
        <div class="alert alert-warning">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="stroke-current shrink-0 h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <span>No agents available. Please create an agent first.</span>
        </div>
      </div>
    );
  }

  const isFormValid =
    form.name.trim() !== '' &&
    form.prompt.trim() !== '' &&
    form.agentId !== '' &&
    (form.schedule.type !== 'one-off' || form.schedule.executeAt) &&
    (form.schedule.type !== 'cron' || form.schedule.expression?.trim());

  const canPreview = form.prompt.trim() !== '' && form.agentId !== '';

  return (
    <form onSubmit={handleSubmit} class="space-y-8">
      {/* Basic Information Section */}
      <div class="space-y-5">
        <div class="form-control">
          <label class="label">
            <span class="label-text font-semibold text-base">
              Workflow Name <span class="text-error">*</span>
            </span>
          </label>
          <input
            type="text"
            class="input input-bordered w-full"
            placeholder="e.g., Daily Status Report"
            value={form.name}
            onInput={(e) => updateField('name', (e.target as HTMLInputElement).value)}
            required
          />
        </div>

        <div class="form-control">
          <label class="label">
            <span class="label-text font-semibold text-base">Description</span>
          </label>
          <textarea
            class="textarea textarea-bordered w-full"
            placeholder="Brief description of what this workflow does"
            value={form.description}
            onInput={(e) => updateField('description', (e.target as HTMLTextAreaElement).value)}
            rows={2}
          />
        </div>

        <div class="form-control">
          <label class="label">
            <span class="label-text font-semibold text-base">
              Agent <span class="text-error">*</span>
            </span>
          </label>
          <select
            class="select select-bordered w-full"
            value={form.agentId}
            onChange={(e) => updateField('agentId', (e.target as HTMLSelectElement).value)}
            required
          >
            <option value="">Select an agent...</option>
            {agents.map((agent) => (
              <option key={agent.id} value={agent.id}>
                {agent.name}
              </option>
            ))}
          </select>
        </div>

        <div class="form-control">
          <label class="label">
            <span class="label-text font-semibold text-base">
              Prompt <span class="text-error">*</span>
            </span>
          </label>
          <textarea
            class="textarea textarea-bordered w-full font-mono text-sm"
            placeholder="What should the agent do? Be specific..."
            value={form.prompt}
            onInput={(e) => updateField('prompt', (e.target as HTMLTextAreaElement).value)}
            rows={6}
            required
          />
          <label class="label">
            <span class="label-text-alt text-base-content/70">
              This prompt will be sent to the agent when the workflow runs
            </span>
          </label>
        </div>
      </div>

      {/* AI Models Section */}
      {supportedModels.length > 0 && (
        <div class="space-y-5">
          <div class="divider font-semibold text-lg my-2">AI Models</div>

          <div class="form-control">
            <label class="label">
              <span class="label-text font-semibold text-base">Primary Model</span>
            </label>
            <select
              class="select select-bordered w-full"
              value={form.primaryModel}
              onChange={(e) => updateField('primaryModel', (e.target as HTMLSelectElement).value)}
            >
              <option value="">Default</option>
              {supportedModels.map((model) => (
                <option key={model.id} value={model.id}>
                  {model.label}
                </option>
              ))}
            </select>
            <label class="label">
              <span class="label-text-alt text-base-content/70">
                Plans and orchestrates the workflow
              </span>
            </label>
          </div>

          <div class="form-control">
            <label class="label">
              <span class="label-text font-semibold text-base">Secondary Model</span>
            </label>
            <select
              class="select select-bordered w-full"
              value={form.secondaryModel}
              onChange={(e) => updateField('secondaryModel', (e.target as HTMLSelectElement).value)}
            >
              <option value="">Default</option>
              {supportedModels.map((model) => (
                <option key={model.id} value={model.id}>
                  {model.label}
                </option>
              ))}
            </select>
            <label class="label">
              <span class="label-text-alt text-base-content/70">
                Executes each step of the workflow
              </span>
            </label>
          </div>
        </div>
      )}

      {/* Schedule Section */}
      <div class="space-y-5">
        <div class="divider font-semibold text-lg my-2">Schedule</div>
        <SchedulePicker schedule={form.schedule} onChange={(s) => updateField('schedule', s)} />
      </div>

      {/* Options Section */}
      <div class="space-y-5">
        <div class="divider font-semibold text-lg my-2">Options</div>
        <div class="form-control">
          <label class="label cursor-pointer justify-start gap-4">
            <input
              type="checkbox"
              class="checkbox checkbox-primary"
              checked={form.enabled}
              onChange={(e) => updateField('enabled', (e.target as HTMLInputElement).checked)}
            />
            <div>
              <span class="label-text font-semibold">Enable workflow</span>
              <p class="text-sm text-base-content/70 mt-1">
                Disabled workflows won't run on schedule (but can still be triggered manually)
              </p>
            </div>
          </label>
        </div>
      </div>

      {/* Preview Section */}
      <div class="space-y-5">
        <div class="divider font-semibold text-lg my-2">Preview</div>

        <div class="space-y-4">
          <button
            type="button"
            class="btn btn-outline btn-wide"
            onClick={handlePreview}
            disabled={previewLoading || !canPreview}
          >
            {previewLoading ? (
              <>
                <span class="loading loading-spinner loading-sm"></span>
                Generating Preview...
              </>
            ) : (
              'Preview TODO List'
            )}
          </button>

          {showPreview && previewTodos.length > 0 && (
            <div class="card bg-base-200 shadow-md">
              <div class="card-body">
                <h3 class="card-title text-base mb-3">TODO List Preview</h3>
                <ul class="space-y-3">
                  {previewTodos.map((todo, idx) => (
                    <li key={idx} class="flex items-start gap-3">
                      <span class="text-primary font-bold text-lg">{idx + 1}.</span>
                      <div class="flex-1">
                        <span class="text-sm">{todo.description}</span>
                        {todo.requiresApproval && (
                          <div class="mt-1">
                            <span class="badge badge-warning badge-sm">Requires Approval</span>
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {showPreview && previewTodos.length === 0 && (
            <div class="alert alert-info">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                class="stroke-current shrink-0 w-6 h-6"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span>No TODO items generated for this workflow</span>
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div class="flex gap-3 justify-end pt-6 border-t border-base-300">
        <button type="button" class="btn btn-ghost" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" class="btn btn-primary btn-wide" disabled={submitting || !isFormValid}>
          {submitting ? (
            <>
              <span class="loading loading-spinner loading-sm"></span>
              {initialData?.workflowId ? 'Updating...' : 'Creating...'}
            </>
          ) : (
            submitLabel
          )}
        </button>
      </div>
    </form>
  );
}
